<script setup>
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps({
  modelValue:  { type: String, default: '' },
  // 'digits' → 6 numeric cells.
  // 'recovery' → 8 alphanumeric cells split as 4 + dash + 4.
  format:      { type: String, default: 'digits' },
  disabled:    { type: Boolean, default: false },
  autofocus:   { type: Boolean, default: true },
})
const emit = defineEmits(['update:modelValue', 'complete'])

const SHAPE = computed(() => props.format === 'recovery'
  ? { length: 8, group: 4, char: /[A-Z0-9]/i, normalize: v => v.toUpperCase() }
  : { length: 6, group: 0, char: /[0-9]/,     normalize: v => v }
)

const cells = ref(Array.from({ length: SHAPE.value.length }, () => ''))
const inputs = ref([])

// Seed cells from incoming modelValue. Strip dash for recovery so we don't
// burn a cell on punctuation; the visual dash is rendered between groups.
function seed(val) {
  const clean = String(val || '').replace(/-/g, '')
  const next = Array.from({ length: SHAPE.value.length }, (_, i) => {
    const ch = clean[i]
    return ch && SHAPE.value.char.test(ch) ? SHAPE.value.normalize(ch) : ''
  })
  cells.value = next
}
seed(props.modelValue)

watch(() => props.modelValue, v => {
  if (joined() !== v) seed(v)
})
watch(() => props.format, () => {
  cells.value = Array.from({ length: SHAPE.value.length }, () => '')
  emitValue()
})

function joined() {
  const raw = cells.value.join('')
  if (props.format === 'recovery' && raw.length > 4) {
    return raw.slice(0, 4) + '-' + raw.slice(4)
  }
  return raw
}

function emitValue() {
  const v = joined()
  emit('update:modelValue', v)
  const filled = cells.value.every(c => c !== '')
  if (filled) emit('complete', v)
}

function focusCell(idx) {
  const el = inputs.value[idx]
  if (el) { el.focus(); el.select?.() }
}

function onInput(idx, e) {
  const raw = String(e.target.value || '')
  // Allow user to type a normal char OR paste a whole code into one cell.
  if (raw.length <= 1) {
    if (raw === '' || SHAPE.value.char.test(raw)) {
      cells.value[idx] = raw ? SHAPE.value.normalize(raw) : ''
      e.target.value = cells.value[idx]
      if (cells.value[idx] && idx < SHAPE.value.length - 1) focusCell(idx + 1)
    } else {
      e.target.value = cells.value[idx]   // reject
    }
  } else {
    // Multi-char paste-into-cell
    distributePaste(raw, idx)
  }
  emitValue()
}

function distributePaste(text, startIdx = 0) {
  const clean = String(text || '').replace(/-/g, '')
  let i = startIdx
  for (const ch of clean) {
    if (i >= SHAPE.value.length) break
    if (!SHAPE.value.char.test(ch)) continue
    cells.value[i] = SHAPE.value.normalize(ch)
    i++
  }
  // Focus the cell after the last one we wrote into
  nextTick(() => focusCell(Math.min(i, SHAPE.value.length - 1)))
}

function onPaste(idx, e) {
  const text = e.clipboardData?.getData('text') || ''
  if (!text) return
  e.preventDefault()
  distributePaste(text, idx)
  emitValue()
}

function onKeydown(idx, e) {
  if (e.key === 'Backspace' && !cells.value[idx] && idx > 0) {
    e.preventDefault()
    cells.value[idx - 1] = ''
    focusCell(idx - 1)
    emitValue()
    return
  }
  if (e.key === 'ArrowLeft' && idx > 0) {
    e.preventDefault()
    focusCell(idx - 1)
  } else if (e.key === 'ArrowRight' && idx < SHAPE.value.length - 1) {
    e.preventDefault()
    focusCell(idx + 1)
  }
}

watch(() => props.autofocus, v => { if (v) nextTick(() => focusCell(0)) }, { immediate: true })
</script>

<template>
  <div class="split-code" :class="`split-code--${format}`" role="group">
    <template v-for="(_, idx) in cells" :key="idx">
      <span
        v-if="SHAPE.group > 0 && idx > 0 && idx % SHAPE.group === 0"
        class="split-code__sep"
        aria-hidden="true"
      >–</span>
      <input
        ref="inputs"
        class="split-code__cell"
        :class="{ 'is-filled': !!cells[idx] }"
        type="text"
        :inputmode="format === 'digits' ? 'numeric' : 'text'"
        :autocomplete="format === 'digits' ? 'one-time-code' : 'off'"
        :maxlength="1"
        :disabled="disabled"
        :value="cells[idx]"
        @input="e => onInput(idx, e)"
        @paste="e => onPaste(idx, e)"
        @keydown="e => onKeydown(idx, e)"
        @focus="e => e.target.select?.()"
      />
    </template>
  </div>
</template>
